import type { ReactNode } from "react";
import {
  GENRES,
  numJP,
  type AgeRating,
  type GenreKey,
  type GenreTone,
  type Work,
} from "../data";
import { workImageUrl } from "../api";
import { useT } from "../i18n";

// =============================================================
// Striped placeholder with monospace caption
// =============================================================

interface PlaceholderProps {
  caption?: string;
  tone?: GenreTone;
  aspect?: string;
  height?: number;
  seed?: number;
  className?: string;
}

export function Placeholder({
  caption = "キービジュアル",
  tone = "other",
  aspect,
  height,
  seed = 0,
  className = "",
}: PlaceholderProps) {
  const offset = (seed * 17) % 40;
  const angle = 110 + ((seed * 7) % 30);
  const style: React.CSSProperties = aspect
    ? { aspectRatio: aspect }
    : height
      ? { height: `${height}px` }
      : {};
  return (
    <div className={`ph ph-tone-${tone} ${className}`} style={style}>
      <svg viewBox="0 0 200 120" preserveAspectRatio="none" aria-hidden>
        <defs>
          <pattern
            id={`stripe-${seed}`}
            patternUnits="userSpaceOnUse"
            width="14"
            height="14"
            patternTransform={`rotate(${angle}) translate(${offset} 0)`}
          >
            <rect width="14" height="14" fill="none" />
            <line x1="0" y1="0" x2="0" y2="14" stroke="rgba(230,234,242,0.04)" strokeWidth="6" />
          </pattern>
          <pattern
            id={`stripe2-${seed}`}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform={`rotate(${-angle / 2})`}
          >
            <rect width="6" height="6" fill="none" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(230,234,242,0.025)" strokeWidth="2" />
          </pattern>
        </defs>
        <rect width="200" height="120" fill={`url(#stripe2-${seed})`} />
        <rect width="200" height="120" fill={`url(#stripe-${seed})`} />
        <circle cx="8" cy="8" r="1.2" fill="rgba(230,234,242,0.18)" />
        <circle cx="192" cy="112" r="1.2" fill="rgba(230,234,242,0.18)" />
      </svg>
      {caption && <span className="ph-caption">{caption}</span>}
    </div>
  );
}

// =============================================================
// WorkThumb: 実サムネがあれば <img>、無ければ Placeholder にフォールバック
// =============================================================

interface WorkThumbProps {
  work: Work;
  caption?: string;
  seed?: number;
  aspect?: string;
  className?: string;
}

export function WorkThumb({ work, caption, seed, aspect, className }: WorkThumbProps) {
  const tone = GENRES[work.genre]?.tone ?? "other";
  const url = workImageUrl(work.id, work.thumbnail);
  if (url) {
    return (
      <div
        className={`ph ph-tone-${tone} ph-image ${className ?? ""}`}
        style={aspect ? { aspectRatio: aspect } : undefined}
      >
        <img src={url} alt={work.title} loading="lazy" />
      </div>
    );
  }
  return (
    <Placeholder
      caption={caption ?? work.title}
      tone={tone}
      seed={seed ?? work.id.charCodeAt(2)}
      aspect={aspect}
      className={className}
    />
  );
}

// =============================================================
// Stars
// =============================================================

export function Stars({ rating, size = 12 }: { rating: number; size?: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span className="stars" style={{ fontSize: size, letterSpacing: "0.04em" }}>
      {Array.from({ length: 5 }).map((_, i) => {
        if (i < full) return <span key={i}>★</span>;
        if (i === full && half) {
          return (
            <span key={i} style={{ position: "relative" }}>
              ★
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "50%",
                  overflow: "hidden",
                  color: "var(--border-2)",
                }}
              >
                ★
              </span>
            </span>
          );
        }
        return (
          <span key={i} style={{ color: "var(--border-2)" }}>
            ★
          </span>
        );
      })}
    </span>
  );
}

// =============================================================
// Badges
// =============================================================

export function GenreBadge({ genre }: { genre: GenreKey | undefined }) {
  if (!genre) return null;
  const g = GENRES[genre];
  if (!g) return null;
  return <span className="badge badge-genre">{g.ja}</span>;
}

export function AgeBadge({ rating }: { rating: AgeRating | undefined }) {
  if (rating === "all") return <span className="badge badge-age-all">全年齢</span>;
  if (rating === "r18") return <span className="badge badge-age">R-18</span>;
  if (rating === "r15")
    return (
      <span
        className="badge badge-age"
        style={{
          color: "#ffaa66",
          borderColor: "rgba(255,170,102,0.3)",
          background: "rgba(255,170,102,0.1)",
        }}
      >
        R-15
      </span>
    );
  return null;
}

export function PriceBadge({ price }: { price: number | undefined }) {
  if (!price || price === 0) return <span className="badge badge-free">無料</span>;
  return <span className="badge badge-price">¥{price.toLocaleString("ja-JP")}</span>;
}

// =============================================================
// Work card
// =============================================================

interface WorkCardProps {
  work: Work;
  onClick?: (work: Work) => void;
  compact?: boolean;
  seed?: number;
}

export function WorkCard({ work, onClick, compact, seed }: WorkCardProps) {
  return (
    <a
      className="wcard"
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(work);
      }}
    >
      <div className="wthumb">
        <WorkThumb work={work} seed={seed} />
        <div className="wbadges">
          <GenreBadge genre={work.genre} />
        </div>
        <div className="wprice">
          <PriceBadge price={work.price} />
        </div>
      </div>
      <div className="wbody">
        <div className="wtitle">{work.title}</div>
        <div className="wauthor">{work.authorName ?? ""}</div>
        {!compact && (
          <div className="wmeta">
            <Stars rating={work.rating ?? 0} size={11} />
            <span className="mono">{(work.rating ?? 0).toFixed(1)}</span>
            <span className="mono" style={{ marginLeft: "auto" }}>
              ▶ {numJP(work.stats?.plays ?? 0)}
            </span>
          </div>
        )}
      </div>
    </a>
  );
}

// =============================================================
// Rank item
// =============================================================

interface RankItemProps {
  rank: { rank: number; prev: number | null };
  work: Work;
  onClick?: (work: Work) => void;
}

export function RankItem({ rank, work, onClick }: RankItemProps) {
  const topClass =
    rank.rank === 1 ? "top-1" : rank.rank === 2 ? "top-2" : rank.rank === 3 ? "top-3" : "";
  const delta = rank.prev == null ? "NEW" : rank.prev - rank.rank;
  const deltaCls =
    rank.prev == null
      ? "up"
      : (delta as number) > 0
        ? "up"
        : (delta as number) < 0
          ? "dn"
          : "flat";
  const deltaText =
    rank.prev == null
      ? "NEW"
      : (delta as number) > 0
        ? `▲${delta}`
        : (delta as number) < 0
          ? `▼${Math.abs(delta as number)}`
          : "−";
  return (
    <a
      className={`rank-item ${topClass}`}
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(work);
      }}
    >
      <div className="rank-num">{String(rank.rank).padStart(2, "0")}</div>
      <WorkThumb
        work={work}
        caption=""
        seed={work.id.charCodeAt(3)}
        className="rank-thumb"
      />
      <div className="rank-body">
        <div className="rank-title">{work.title}</div>
        <div className="rank-author">
          {work.authorName ?? ""} ·{" "}
          <span style={{ color: "var(--sub)" }}>{work.shortDescription}</span>
        </div>
      </div>
      <div className="rank-stats">
        <span className={`delta ${deltaCls}`}>{deltaText}</span>
        <span>▶ {numJP(work.stats?.plays ?? 0)}</span>
      </div>
    </a>
  );
}

// =============================================================
// Footer
// =============================================================

export function Footer() {
  const t = useT();
  return (
    <footer className="gfooter">
      <div className="container">
        <div>
          <div className="fbrand">Pix.</div>
          <div className="fdesc">
            {t("foot.tagline")}
            <br />
            v0.5.0 · build 26.05.14
          </div>
        </div>
        <div className="fcol">
          <h4>{t("foot.about")}</h4>
          <a href="#">{t("foot.aboutPix")}</a>
          <a href="#">{t("foot.company")}</a>
          <a href="#">{t("foot.careers")}</a>
          <a href="#">{t("foot.press")}</a>
        </div>
        <div className="fcol">
          <h4>{t("foot.legal")}</h4>
          <a href="#">{t("foot.tos")}</a>
          <a href="#">{t("foot.privacy")}</a>
          <a href="#">{t("foot.tokushoho")}</a>
          <a href="#">{t("foot.ageGuide")}</a>
        </div>
        <div className="fcol">
          <h4>{t("foot.help")}</h4>
          <a href="#">{t("foot.contact")}</a>
          <a href="#">{t("foot.faq")}</a>
          <a href="#">{t("foot.status")}</a>
          <a href="#">{t("foot.discord")}</a>
        </div>
        <div className="fcol">
          <h4>{t("foot.studio")}</h4>
          <a href="https://studio.pixadv.net">studio.pixadv.net</a>
          <a href="https://play.pixadv.net">play.pixadv.net</a>
          <a href="#">{t("foot.apiDocs")}</a>
          <a href="#">{t("foot.changelog")}</a>
        </div>
        <div className="fnote">
          <span>{t("foot.copyright")}</span>
          <span>{t("foot.langNote")}</span>
        </div>
      </div>
    </footer>
  );
}

// =============================================================
// Empty / Snackbar
// =============================================================

export function Empty({
  title,
  desc,
  icon = "//",
  action,
}: {
  title?: string;
  desc?: string;
  icon?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <div className="empty-title">{title ?? "まだありません"}</div>
      {desc && <div className="empty-desc">{desc}</div>}
      {action}
    </div>
  );
}

export function Snackbar({
  kind = "info",
  children,
  onClose,
}: {
  kind?: "info" | "success" | "warn";
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className={`snackbar ${kind}`}>
      <span>{children}</span>
      <button className="sx" onClick={onClose}>
        ×
      </button>
    </div>
  );
}

/** ローディングプレースホルダ(skeleton) */
export function LoadingBlock({ height = 200 }: { height?: number }) {
  return (
    <div
      className="skeleton"
      style={{ height, borderRadius: "var(--r-md)" }}
    />
  );
}
